// R11P GEOLOGICAL P0.5-P9C-FIX — Public GIS response contract.
//
// Pure and dependency-free so the contract is proven behaviourally under
// `node --test`. The authoritative geo-api catalogue is
//
//   GET /api/gis/layers  ->  { layers: GisLayerRow[], count: number }
//
// where each row carries the gis_layers columns in snake_case (geometry_type
// is the lowercase gis_geometry_type enum). P9C wrongly expected a bare array
// and a camelCase `geometryType`, so every successful response rendered as
// "no public layers".
//
// Invariants:
//   • only a well-formed envelope is a success — anything else is UNAVAILABLE
//     (null), never a measured zero;
//   • `{ layers: [], count: 0 }` is the one genuine empty;
//   • `count` is validated but never trusted as a display number;
//   • a row is shown on the anonymous channel only when the SERVER labels it
//     classification === 'public' — nothing is inferred client-side.

const isObj = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v);

/**
 * Validate the catalogue envelope and return its raw rows, or null when the
 * response is not the contract (malformed body, missing/non-array `layers`,
 * or a `count` that is present but not a non-negative integer).
 */
export function parsePublicLayerEnvelope(body: unknown): unknown[] | null {
  if (!isObj(body)) return null;
  if (!Array.isArray(body.layers)) return null;
  if (body.count !== undefined) {
    const c = body.count;
    if (typeof c !== 'number' || !Number.isInteger(c) || c < 0) return null;
  }
  return body.layers;
}

/** The server's gis_geometry_type enum -> GeoJSON geometry type names. */
const GEOMETRY_TYPES: Readonly<Record<string, string>> = {
  point: 'Point',
  multipoint: 'MultiPoint',
  linestring: 'LineString',
  multilinestring: 'MultiLineString',
  polygon: 'Polygon',
  multipolygon: 'MultiPolygon',
  geometrycollection: 'GeometryCollection',
  raster: 'Raster',
};

/**
 * Normalise the server's `geometry_type` for display. Only the known enum
 * values are mapped (case-insensitively); anything else is undefined — a
 * geometry type is never fabricated.
 */
export function normalizeGeometryType(raw: unknown): string | undefined {
  if (typeof raw !== 'string') return undefined;
  return GEOMETRY_TYPES[raw.trim().toLowerCase()];
}

/**
 * True only when the server itself labelled the row public. A missing or
 * different classification is never defaulted to public on this channel.
 */
export function isServerPublicRow(raw: unknown): boolean {
  return isObj(raw) && raw.classification === 'public';
}
